/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { createElement, forwardRef, isValidElement, useCallback, useEffect, useRef } from 'react';
import { ClassNames, jsx } from '@emotion/react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useId } from '@atlaskit/ds-lib/use-id';
import FocusRing from '@atlaskit/focus-ring';
import { fg } from '@atlaskit/platform-feature-flags';
import { N0, N70A } from '@atlaskit/theme/colors';
import AvatarImage from './avatar-image';
import { ACTIVE_SCALE_FACTOR, AVATAR_RADIUS, AVATAR_SIZES, BORDER_WIDTH } from './constants';
import { useAvatarContext } from './context';
import { PresenceWrapper } from './presence';
import { StatusWrapper } from './status';
import { getButtonProps, getCustomElement, getLinkProps } from './utilities';
const packageName = "@atlaskit/avatar";
const packageVersion = "21.17.5";
const getTestId = (testId, children) => !children ? {
  'data-testid': `${testId}--inner`
} : {
  testId: `${testId}--inner`
};

// eslint-disable-next-line @repo/internal/react/consistent-types-definitions

const getStyles = (css, {
  size,
  radius,
  appearance,
  borderColor = fg('platform-component-visual-refresh') ? "var(--ds-surface, #FFFFFF)" : `var(--ds-surface-overlay, ${N0})`,
  stackIndex,
  isInteractive,
  isDisabled
}) =>
//eslint-disable-next-line @repo/internal/react/no-css-string-literals
css`
		height: ${size}px;
		width: ${size}px;
		align-items: stretch;
		background-color: ${borderColor};
		border-radius: ${appearance === 'circle' ? '50%' : `${radius}px`};
		box-sizing: content-box;
		cursor: inherit;
		display: flex;
		flex-direction: column;
		justify-content: center;
		outline: none;
		overflow: hidden;
		position: static;
		transform: translateZ(0);
		transition:
			transform 200ms,
			opacity 200ms;
		box-shadow: 0 0 0 ${BORDER_WIDTH}px ${borderColor};
		border: none;
		margin: ${"var(--ds-space-025, 2px)"};
		padding: ${"var(--ds-space-0, 0px)"};

		&::-moz-focus-inner {
			border: 0;
			margin: ${"var(--ds-space-0, 0px)"};
			padding: ${"var(--ds-space-0, 0px)"};
		}

		&::after {
			background-color: transparent;
			inset: 0px;

			/* Added border-radius style to fix hover issue in safari */
			border-radius: ${appearance === 'circle' ? '50%' : `${radius}px`};
			content: ' ';
			opacity: 0;
			pointer-events: none;
			position: absolute;
			transition: opacity 200ms;
			width: 100%;
		}

		:focus-visible {
			box-shadow: none;
		}

		${stackIndex && `position: relative;`}

		${isInteractive && `
      cursor: pointer;

      :hover {
        &::after {
          background-color: ${`var(--ds-interaction-hovered, ${N70A})`};
          opacity: 1;
        }
      }

      :active {
        &::after {
          background-color: ${`var(--ds-interaction-pressed, ${N70A})`};
          opacity: 1;
        }
      }

      :active {
        transform: scale(${ACTIVE_SCALE_FACTOR});
      }

      @media screen and (forced-colors: active) {
        &:focus-visible {
          outline: 1px solid
        }
      }
    `}

    ${isDisabled && `
        cursor: not-allowed;

        &::after {
          opacity: ${"var(--ds-opacity-disabled, 0.7)"};
          pointer-events: none;
          background-color: ${`var(--ds-surface, ${N0})`};
        }
      `}
	`;

/**
 * __Avatar__
 *
 * An avatar is a visual representation of a user or entity.
 *
 * - [Examples](https://atlassian.design/components/avatar/examples)
 * - [Code](https://atlassian.design/components/avatar/code)
 * - [Usage](https://atlassian.design/components/avatar/usage)
 */
const Avatar = /*#__PURE__*/forwardRef(({
  analyticsContext,
  appearance = 'circle',
  label,
  borderColor,
  children,
  href,
  isDisabled,
  name,
  onClick,
  presence,
  size: sizeProp,
  src,
  stackIndex,
  status,
  target,
  testId,
  as: AvatarContainer = 'div'
}, ref) => {
  var _ref;
  const {
    createAnalyticsEvent
  } = useAnalyticsEvents();
  const context = useAvatarContext();
  const size = (_ref = sizeProp !== null && sizeProp !== void 0 ? sizeProp : context === null || context === void 0 ? void 0 : context.size) !== null && _ref !== void 0 ? _ref : 'medium';
  const customPresenceNode = /*#__PURE__*/isValidElement(presence) ? presence : null;
  const customStatusNode = /*#__PURE__*/isValidElement(status) ? status : null;
  const isValidIconSize = size !== 'xxlarge' && size !== 'xsmall';
  const lastAnalytics = useRef(analyticsContext);
  const labelId = useId();
  useEffect(() => {
    lastAnalytics.current = analyticsContext;
  }, [analyticsContext]);
  const onClickHandler = useCallback(event => {
    if (isDisabled || typeof onClick !== 'function') {
      return;
    }
    const analyticsEvent = createAnalyticsEvent({
      action: 'clicked',
      actionSubject: 'avatar',
      attributes: {
        componentName: 'avatar',
        packageName,
        packageVersion
      }
    });

    /**
     * To avoid wrapping this component in AnalyticsContext we manually
     * push the parent context's meta data into the context.
     */
    const context = {
      componentName: 'avatar',
      packageName,
      packageVersion,
      ...lastAnalytics.current
    };
    analyticsEvent.context.push(context);

    /**
     * Replicating the logic in the `withAnalyticsEvents` HOC.
     */
    const clone = analyticsEvent.clone();
    if (clone) {
      clone.fire('atlaskit');
    }
    onClick(event, analyticsEvent);
  }, [createAnalyticsEvent, isDisabled, onClick]);
  const componentProps = () => {
    if (isDisabled) {
      return {
        disabled: true
      };
    }

    // return only relevant props for either anchor or button elements
    return {
      ...(href && getLinkProps(href, target)),
      ...(onClick && !href ? getButtonProps(onClickHandler) : {
        onClick
      })
    };
  };
  const isPresence = isValidIconSize && presence && !status;
  const isStatus = isValidIconSize && status;

  // add presence or status to the label by default if presence and status are passed as a string
  // if status or presence are nodes this is not added to the label by default
  const defaultLabel = [name, isStatus && !customStatusNode && `(${status})`, isPresence && !customPresenceNode && `(${presence})`].filter(Boolean).join(' ');
  const isInteractive = onClick || href || isDisabled;
  const containerShouldBeImage = Boolean(!isInteractive && defaultLabel);
  return jsx(AvatarContainer, {
    "data-testid": testId,
    role: containerShouldBeImage ? 'img' : undefined,
    "aria-labelledby": containerShouldBeImage ? labelId : undefined,
    style: {
      // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
      display: 'inline-block',
      // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
      position: 'relative',
      // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
      outline: 0,
      zIndex: stackIndex
    }
  }, jsx(ClassNames, null, ({
    css
  }) => {
    const props = {
      className: getStyles(css, {
        size: AVATAR_SIZES[size],
        radius: AVATAR_RADIUS[size],
        appearance,
        borderColor,
        stackIndex,
        isInteractive: Boolean(href || onClick) && !isDisabled,
        isDisabled
      }),
      ...componentProps(),
      ...(testId && getTestId(testId, children)),
      ...((isInteractive || children) && {
        'aria-label': label || defaultLabel
      }),
      children: jsx(AvatarImage
      // Only pass in the name if an image is provided and the
      // container is not being used as an `img` role
      , {
        alt: !containerShouldBeImage && src ? name : undefined,
        appearance: appearance,
        size: size,
        src: src,
        testId: testId
      }),
      ref
    };
    if (children) {
      return children(props);
    }
    const element = getCustomElement(isDisabled, href, onClick);
    return element === 'a' || element === 'button' ? jsx(FocusRing, null, /*#__PURE__*/createElement(element, props)) : ( /*#__PURE__*/createElement(element, props));
  }), isPresence && jsx(PresenceWrapper, {
    appearance: appearance,
    size: size,
    presence: typeof presence === 'string' ? presence : undefined,
    testId: testId
  }, customPresenceNode), isStatus && jsx(StatusWrapper, {
    appearance: appearance,
    size: size,
    borderColor: borderColor,
    status: typeof status === 'string' ? status : undefined,
    testId: testId
  }, customStatusNode), containerShouldBeImage ? jsx("span", {
    "data-testid": testId && `${testId}--label`,
    id: labelId,
    hidden: true
  }, defaultLabel) : undefined);
});
Avatar.displayName = 'Avatar';
export default Avatar;